import { TTypeOfCodec } from "../../../shared/src/codecs/codec";
import { literal } from "../../../shared/src/codecs/types/literal";
import { TUnionCodec, union } from "../../../shared/src/codecs/types/union";
import { requireExhaustive } from "../../../shared/src/util";
import { TRequiredCodec, required } from "../../../shared/src/codecs/types/required";
import { TArrayCodec, array } from "../../../shared/src/codecs/types/array";
import { TFormCodec, form } from "../../../shared/src/codecs/types/form";
import { uuid } from "../../../shared/src/codecs/types/uuid";
import { string } from "../../../shared/src/codecs/types/string";
import { dateTime } from "../../../shared/src/codecs/types/dateTime";
import { date } from "../../../shared/src/codecs/types/date";
import { nullCodec } from "../../../shared/src/codecs/types/nullCodec";
import { positiveInteger } from "../../../shared/src/codecs/types/positiveInteger";
import { PlainUser } from "../User";
import {
    RoadblockAction,
    RoadblockAwaitableEntity,
    RoadblockCheckListItemManual,
    RoadblockChecklistItemAutomatic,
    RoadblockStatus,
} from "./RoadblockForm";
import { ChatsMessageForm } from "./ChatsMessageForm";
import { StaffUser2 } from "../User";
import { UserName } from "../overloads/UserName";
import { TUuidObjectCodec, UuidObject } from "../Id";
import { boolean } from "../../../shared/src/codecs/types/boolean";

export const ListingRoadblockType = union([
    literal("win_instruction"),
    literal("market_prep_ready"),
    literal("under_offer"),
    literal("completed"),
    literal("works"),
]);
export type TListingRoadblockTypeCodec = typeof ListingRoadblockType;
export type TListingRoadblockType = TTypeOfCodec<TListingRoadblockTypeCodec>;

export const ListingRoadblockType_displayString = (type: TListingRoadblockType): string => {
    switch (type) {
        case "win_instruction":
            return "Win instruction";
        case "market_prep_ready":
            return "Market prep ready";
        case "under_offer":
            return "Under offer";
        case "completed":
            return "Completed";
        case "works":
            return "Works";
        default:
            return requireExhaustive(type);
    }
};

// Stored as plain varchar, matching the legal side. The reader hard-decodes the whole
// checklist array, so a row whose literal is no longer a member here fails the decode for
// the entire listing — renaming or removing one REQUIRES a data migration in the same PR
// (the 2026-07-07 incident).
//
// `ad_hoc` is the one staff-authored literal: several may sit on one roadblock,
// distinguished by `manual_item_name`, which is why the unique index on
// (roadblock, type) is partial on `manual_item_name IS NULL`.
export const ListingRoadblockChecklistItemType = union([
    literal("ad_hoc"),

    literal("photos_uploaded"),
    literal("video_tour_linked"),
    literal("description_proofread"),
    literal("marketing_specifics_signed_off"),
    literal("portal_admin_links_entered"),
    literal("sign_erected"),

    literal("key_strategy_chosen"),
    literal("keysafe_fitted"),
    literal("keynest_setup"),
    literal("keys_delivered"),

    literal("valuation_sent"),
    literal("instruction_won"),

    literal("viewings_strategy_chosen"),

    literal("potential_rent_known"),
    literal("renovation_estimate_verified"),

    literal("live_on_portals"),

    literal("buyer_id_check_complete"),
    literal("buyer_funds_verified"),
    literal("buyer_conveyancer_captured"),
    literal("seller_solicitor_kicked_off"),
    literal("buyer_solicitor_kicked_off"),

    literal("contracts_received"),
    literal("mortgage_application_submitted"),
    literal("mortgage_offer_received"),
    literal("survey_booked"),
    literal("enquiries_raised"),
    literal("enquiries_resolved"),
    literal("exchange_date_set"),
    literal("completion_date_set"),
    literal("sale_completed"),
    literal("keys_released"),
    literal("referral_fee_paid"),
    literal("meter_readings_shared"),
]);
export type TListingRoadblockChecklistItemTypeCodec = typeof ListingRoadblockChecklistItemType;
export type TListingRoadblockChecklistItemType = TTypeOfCodec<TListingRoadblockChecklistItemTypeCodec>;

export const ListingRoadblockNextItem = required({
    type: ListingRoadblockChecklistItemType,
    display_name: string(),
});
export type TListingRoadblockNextItemCodec = typeof ListingRoadblockNextItem;
export type TListingRoadblockNextItem = TTypeOfCodec<TListingRoadblockNextItemCodec>;

export const ListingRoadblocksListItem = required({
    id: uuid(),
    listing_id: uuid(),
    listing_roadblock_type: ListingRoadblockType,
    status: RoadblockStatus,
    sla: dateTime(),
    opened_on: union([dateTime(), nullCodec()]),
    blocked_till: union([dateTime(), nullCodec()]),
    blocked_waiting_on: union([string(), nullCodec()]),
    blocked_waiting_on_is_internal: boolean(),
    last_closed_on: union([dateTime(), nullCodec()]),
    unresolved_count: positiveInteger(),
    next_item: union([ListingRoadblockNextItem, nullCodec()]),
    assigned_to_user: union([PlainUser, nullCodec()]),
});
export type TListingRoadblocksListItemCodec = typeof ListingRoadblocksListItem;
export type TListingRoadblocksListItem = TTypeOfCodec<TListingRoadblocksListItemCodec>;

export const ListingRoadblocksDashboardItem = required({
    id: uuid(),
    listing_id: uuid(),
    listing_roadblock_type: ListingRoadblockType,
    status: RoadblockStatus,
    address: string(),
    sellers: string(),
    sla: dateTime(),
    unresolved_count: positiveInteger(),
    next_item: union([ListingRoadblockNextItem, nullCodec()]),
    assigned_to_user: union([PlainUser, nullCodec()]),
    blocked_till: union([dateTime(), nullCodec()]),
    blocked_waiting_on: union([string(), nullCodec()]),
    blocked_waiting_on_is_internal: boolean(),
});
export type TListingRoadblocksDashboardItemCodec = typeof ListingRoadblocksDashboardItem;
export type TListingRoadblocksDashboardItem = TTypeOfCodec<TListingRoadblocksDashboardItemCodec>;

export const ListingRoadblocksDashboardDate = union([
    required({ type: literal("overdue") }),
    required({ type: literal("iso"), date: dateTime() }),
]);
export type TListingRoadblocksDashboardDateCodec = typeof ListingRoadblocksDashboardDate;

export const ListingRoadblocksDashboardFilters = required({
    assigned_to_user_id: union([uuid(), nullCodec()]),
    date: ListingRoadblocksDashboardDate,
    listing_roadblock_type: union([nullCodec(), ListingRoadblockType]),
    checklist_item_type: union([nullCodec(), ListingRoadblockChecklistItemType]),
    offset: positiveInteger(),
    limit: positiveInteger(),
});
export type TListingRoadblocksDashboardFiltersCodec = typeof ListingRoadblocksDashboardFilters;
export type TListingRoadblocksDashboardFilters = TTypeOfCodec<TListingRoadblocksDashboardFiltersCodec>;

export const ListingRoadblocksDashboardForm: TFormCodec<
    TListingRoadblocksDashboardFiltersCodec,
    TRequiredCodec<{
        roadblocks: TArrayCodec<TListingRoadblocksDashboardItemCodec>,
        assignable_users: TArrayCodec<typeof PlainUser>,
        awaitable_entities: TArrayCodec<typeof RoadblockAwaitableEntity>,
    }>
> = form(
    ListingRoadblocksDashboardFilters,
    required({
        roadblocks: array(ListingRoadblocksDashboardItem),
        assignable_users: array(PlainUser),
        awaitable_entities: array(RoadblockAwaitableEntity),
    }),
);
export type TListingRoadblocksDashboardFormCodec = typeof ListingRoadblocksDashboardForm;
export type TListingRoadblocksDashboardForm = TTypeOfCodec<TListingRoadblocksDashboardFormCodec>;

export const ListingRoadblocksFilters = required({
    listing_id: uuid(),
});
export type TListingRoadblocksFiltersCodec = typeof ListingRoadblocksFilters;

export const ListingRoadblocksForm: TFormCodec<
    TListingRoadblocksFiltersCodec,
    TRequiredCodec<{
        roadblocks: TArrayCodec<TListingRoadblocksListItemCodec>,
        assignable_users: TArrayCodec<typeof PlainUser>,
        awaitable_entities: TArrayCodec<typeof RoadblockAwaitableEntity>,
    }>
> = form(
    ListingRoadblocksFilters,
    required({
        roadblocks: array(ListingRoadblocksListItem),
        assignable_users: array(PlainUser),
        awaitable_entities: array(RoadblockAwaitableEntity),
    }),
);
export type TListingRoadblocksFormCodec = typeof ListingRoadblocksForm;
export type TListingRoadblocksForm = TTypeOfCodec<TListingRoadblocksFormCodec>;

export const ListingRoadblockDetails = required({
    listing_id: uuid(),
    chats_id: uuid(),
    listing_roadblock_type: ListingRoadblockType,
    created_at: dateTime(),
    status: RoadblockStatus,
    sla: dateTime(),
    blocked_till: union([nullCodec(), dateTime()]),
    blocked_by_user: union([nullCodec(), UserName]),
    blocked_waiting_on: string(),
    blocked_waiting_on_is_internal: boolean(),
    resolved_by_user: union([nullCodec(), UserName]),
    resolved_on: union([nullCodec(), dateTime()]),
});
export type TListingRoadblockDetailsCodec = typeof ListingRoadblockDetails;

export const ListingRoadblockDetailForm: TFormCodec<
    TUuidObjectCodec,
    TRequiredCodec<{
        details: TListingRoadblockDetailsCodec,
        actions: TArrayCodec<typeof RoadblockAction>,
        participants: TArrayCodec<typeof StaffUser2>,
        awaitable_entities: TArrayCodec<typeof RoadblockAwaitableEntity>,
        checklist_items_automatic: TArrayCodec<typeof RoadblockChecklistItemAutomatic>,
        checklist_items_manual: TArrayCodec<typeof RoadblockCheckListItemManual>,
        chat_message_form: typeof ChatsMessageForm,
    }>
> = form(
    UuidObject,
    required({
        details: ListingRoadblockDetails,
        actions: array(RoadblockAction),
        participants: array(StaffUser2),
        awaitable_entities: array(RoadblockAwaitableEntity),
        checklist_items_automatic: array(RoadblockChecklistItemAutomatic),
        checklist_items_manual: array(RoadblockCheckListItemManual),
        chat_message_form: ChatsMessageForm,
    }),
);
export type TListingRoadblockDetailFormCodec = typeof ListingRoadblockDetailForm;
export type TListingRoadblockDetailForm = TTypeOfCodec<TListingRoadblockDetailFormCodec>;

export const ListingRoadblocksDashboardWeekCountsDay = required({
    date: date(),
    roadblocks_count: positiveInteger(),
});

export const ListingRoadblocksDashboardWeekCountsForm = form(
    required({
        assigned_to_user_id: union([uuid(), nullCodec()]),
        listing_roadblock_type: union([nullCodec(), ListingRoadblockType]),
        checklist_item_type: union([nullCodec(), ListingRoadblockChecklistItemType]),
    }),
    required({
        days: array(ListingRoadblocksDashboardWeekCountsDay),
        overdue: required({ roadblocks_count: positiveInteger() }),
        cleared_today: required({ roadblocks_count: positiveInteger() }),
    }),
);
export type TListingRoadblocksDashboardWeekCountsFormCodec = typeof ListingRoadblocksDashboardWeekCountsForm;
export type TListingRoadblocksDashboardWeekCountsForm = TTypeOfCodec<TListingRoadblocksDashboardWeekCountsFormCodec>;
